if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.currentIndex = 0;
        this.mediaItems = [];

        /* =====================================================
           PREVENT CRAVE FROM CLOSING WHEN ARROWS ARE CLICKED
           ===================================================== */

        this.addEventListener(
          'pointerup',
          (event) => {
            if (event.target.closest('.ceuticoz-zoom-arrow')) {
              event.stopPropagation();
            }
          },
          true
        );


        /* =====================================================
           ARROW CONTROLS
           ===================================================== */

        this.addEventListener('click', (event) => {
          const prev = event.target.closest('.ceuticoz-zoom-prev');
          const next = event.target.closest('.ceuticoz-zoom-next');

          if (prev) {
            event.preventDefault();
            event.stopPropagation();
            this.previousMedia();
          }

          if (next) {
            event.preventDefault();
            event.stopPropagation();
            this.nextMedia();
          }
        });


        /* =====================================================
           KEYBOARD CONTROLS
           ===================================================== */

        this.addEventListener('keydown', (event) => {
          if (!this.hasAttribute('open')) return;

          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.previousMedia();
          }

          if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.nextMedia();
          }
        });


        /* =====================================================
           PINCH TO ZOOM VARIABLES
           ===================================================== */

        this.pinchStartDistance = 0;
        this.pinchStartScale = 1;
        this.zoomScale = 1;


        /* =====================================================
           MOBILE PINCH START
           ===================================================== */

        this.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length !== 2) return;

            const dx =
              event.touches[0].clientX -
              event.touches[1].clientX;

            const dy =
              event.touches[0].clientY -
              event.touches[1].clientY;

            this.pinchStartDistance = Math.sqrt(
              dx * dx + dy * dy
            );

            this.pinchStartScale = this.zoomScale;
          },
          { passive: true }
        );


        /* =====================================================
           MOBILE PINCH MOVE
           ===================================================== */

        this.addEventListener(
          'touchmove',
          (event) => {
            if (event.touches.length !== 2) return;

            event.preventDefault();

            const dx =
              event.touches[0].clientX -
              event.touches[1].clientX;

            const dy =
              event.touches[0].clientY -
              event.touches[1].clientY;

            const distance = Math.sqrt(
              dx * dx + dy * dy
            );

            if (!this.pinchStartDistance) return;

            const scaleChange =
              distance / this.pinchStartDistance;

            this.zoomScale =
              this.pinchStartScale * scaleChange;

            /* Maximum 4x zoom */
            this.zoomScale = Math.max(
              1,
              Math.min(4, this.zoomScale)
            );

            this.applyZoom();
          },
          { passive: false }
        );


        /* =====================================================
           MOBILE PINCH END
           ===================================================== */

        this.addEventListener(
          'touchend',
          (event) => {
            if (event.touches.length < 2) {
              this.pinchStartDistance = 0;
            }
          },
          { passive: true }
        );
      }


      /* =====================================================
         OPEN MODAL
         ===================================================== */

      show(opener) {
        super.show(opener);

        this.setupSlider();

        this.resetZoom();

        const mediaId =
          opener?.getAttribute('data-media-id');

        this.currentIndex =
          this.mediaItems.findIndex(
            (item) =>
              item.getAttribute('data-media-id') === mediaId
          );

        if (this.currentIndex < 0) {
          this.currentIndex = 0;
        }

        this.updateSlider(false);
      }


      /* =====================================================
         SETUP SLIDER
         ===================================================== */

      setupSlider() {
        this.container =
          this.querySelector(
            '.product-media-modal__content'
          );

        if (!this.container) return;

        this.mediaItems =
          Array.from(
            this.container.querySelectorAll(
              '[data-media-id]'
            )
          );

        if (!this.mediaItems.length) return;


        /* Create Previous button */

        if (
          !this.querySelector(
            '.ceuticoz-zoom-prev'
          )
        ) {
          const prev =
            document.createElement('button');

          prev.type = 'button';

          prev.className =
            'ceuticoz-zoom-arrow ceuticoz-zoom-prev';

          prev.setAttribute(
            'aria-label',
            'Previous image'
          );

          prev.innerHTML = '&#10094;';

          const dialog =
            this.querySelector(
              '.product-media-modal__dialog'
            );

          if (dialog) {
            dialog.appendChild(prev);
          }
        }


        /* Create Next button */

        if (
          !this.querySelector(
            '.ceuticoz-zoom-next'
          )
        ) {
          const next =
            document.createElement('button');

          next.type = 'button';

          next.className =
            'ceuticoz-zoom-arrow ceuticoz-zoom-next';

          next.setAttribute(
            'aria-label',
            'Next image'
          );

          next.innerHTML = '&#10095;';

          const dialog =
            this.querySelector(
              '.product-media-modal__dialog'
            );

          if (dialog) {
            dialog.appendChild(next);
          }
        }


        /* Create counter */

        if (
          !this.querySelector(
            '.ceuticoz-zoom-counter'
          )
        ) {
          const counter =
            document.createElement('div');

          counter.className =
            'ceuticoz-zoom-counter';

          counter.innerHTML =
            '<span class="ceuticoz-zoom-current">1</span>' +
            ' / ' +
            '<span class="ceuticoz-zoom-total">' +
            this.mediaItems.length +
            '</span>';

          const dialog =
            this.querySelector(
              '.product-media-modal__dialog'
            );

          if (dialog) {
            dialog.appendChild(counter);
          }
        }
      }


      /* =====================================================
         SHOW CURRENT SLIDE
         ===================================================== */

      updateSlider(smooth = true) {
        if (
          !this.container ||
          !this.mediaItems.length
        ) {
          return;
        }


        this.mediaItems.forEach(
          (item, index) => {
            item.classList.add(
              'ceuticoz-slide'
            );

            if (
              index === this.currentIndex
            ) {
              item.classList.add(
                'active'
              );
            } else {
              item.classList.remove(
                'active'
              );
            }
          }
        );


        const slide =
          this.mediaItems[
            this.currentIndex
          ];

        if (!slide) return;


        this.container.scrollTo({
          left: slide.offsetLeft,
          behavior: smooth
            ? 'smooth'
            : 'auto'
        });


        /* Update counter */

        const current =
          this.querySelector(
            '.ceuticoz-zoom-current'
          );

        if (current) {
          current.textContent =
            this.currentIndex + 1;
        }


        const total =
          this.querySelector(
            '.ceuticoz-zoom-total'
          );

        if (total) {
          total.textContent =
            this.mediaItems.length;
        }


        /* Load video if required */

        const template =
          slide.querySelector('template');

        if (
          slide.nodeName ===
            'DEFERRED-MEDIA' &&
          template &&
          template.content.querySelector(
            '.js-youtube'
          )
        ) {
          slide.loadContent();
        }
      }


      /* =====================================================
         NEXT IMAGE
         ===================================================== */

      nextMedia() {
        if (!this.mediaItems.length) {
          return;
        }

        this.resetZoom();

        this.currentIndex++;

        if (
          this.currentIndex >=
          this.mediaItems.length
        ) {
          this.currentIndex = 0;
        }

        this.updateSlider(true);
      }


      /* =====================================================
         PREVIOUS IMAGE
         ===================================================== */

      previousMedia() {
        if (!this.mediaItems.length) {
          return;
        }

        this.resetZoom();

        this.currentIndex--;

        if (this.currentIndex < 0) {
          this.currentIndex =
            this.mediaItems.length - 1;
        }

        this.updateSlider(true);
      }


      /* =====================================================
         APPLY PINCH ZOOM
         ===================================================== */

      applyZoom() {
        if (
          !this.mediaItems ||
          !this.mediaItems.length
        ) {
          return;
        }

        const activeSlide =
          this.mediaItems[
            this.currentIndex
          ];

        if (!activeSlide) return;


        const image =
          activeSlide.matches('img')
            ? activeSlide
            : activeSlide.querySelector(
                'img'
              );

        if (!image) return;


        image.style.transform =
          `scale(${this.zoomScale})`;

        image.style.transformOrigin =
          'center center';
      }


      /* =====================================================
         RESET ZOOM
         ===================================================== */

      resetZoom() {
        this.zoomScale = 1;

        this.pinchStartDistance = 0;

        if (!this.mediaItems) {
          return;
        }


        this.mediaItems.forEach(
          (slide) => {
            const image =
              slide.matches('img')
                ? slide
                : slide.querySelector(
                    'img'
                  );

            if (image) {
              image.style.transform =
                'scale(1)';

              image.style.transformOrigin =
                'center center';
            }
          }
        );
      }
    }
  );
}